import React, { useState, useMemo, useCallback } from 'react';
import { useContext } from 'react';
import { FilterSettings } from './Table.interfaces';
import { SortingState } from '@tanstack/react-table';

type TableViewStateContextProviderReturn<T> = {
	getViewState: (tableId?: string) => TableViewState<T> | undefined;
	updateViewState: (
		tableId: string | undefined,
		updates: Partial<TableViewState<T>>,
	) => void;
};

export type TableViewState<T> = {
	filters?: FilterSettings<T>;
	pageIndex?: number;
	pageSize?: number;
	search?: string;
	sorting?: SortingState;
};

const TableViewStateContext =
	React.createContext<TableViewStateContextProviderReturn<any> | null>(null);

export function TableViewStateContextProvider<T>({
	children,
}: React.PropsWithChildren) {
	const [tableViewState, setTableViewState] =
		useState<Record<string, TableViewState<T>>>();

	const getViewState = useCallback(
		(tableId?: string) => {
			if (!tableId) return undefined;
			return tableViewState?.[tableId];
		},
		[tableViewState],
	);

	const updateViewState = useCallback(
		(tableId: string | undefined, updates: Partial<TableViewState<T>>) => {
			if (!tableId) return;

			setTableViewState((prev) => ({
				...prev,
				[tableId]: {
					...prev?.[tableId],
					...updates,
				},
			}));
		},
		[],
	);

	const contextValue = useMemo(
		() => ({
			getViewState,
			updateViewState,
		}),
		[getViewState, updateViewState],
	);

	return (
		<TableViewStateContext.Provider value={contextValue}>
			{children}
		</TableViewStateContext.Provider>
	);
}

export function useTableViewStateContext<T>() {
	const context = useContext<TableViewStateContextProviderReturn<T>>(
		TableViewStateContext as unknown as React.Context<
			TableViewStateContextProviderReturn<T>
		>,
	);
	if (!context) {
		throw new Error(
			'useTableViewStateContext must be used under TableViewStateContextProvider',
		);
	}
	return context;
}
